import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from '../../services/axiosInstance';
import maCateImg from "../../assets/img/m-a-cate.png";
import TopBtn from "../../components/web/layoutsadmin/TopBtn";
import MsgSelectModal from "../../components/web/modal/MsgSelectModal";
import {useWebSocket} from "../../services/WebsocketContext";
import {FiExternalLink, FiPlus, FiTrash2} from "react-icons/fi";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  관리자 [상점관리 > 영업현황]
 * ════════════════════════════════════════════════════════════════════════════════
 *  한국의 쇼핑몰들을 상대로 주문연동 API 영업을 진행하기 위한 목록/체크 화면.
 *
 *  [실시간 공유]
 *   여러 관리자가 동시에 보는 화면이므로 저장/삭제 시 서버가 '/topic/storeSales' 로
 *   브로드캐스트하고, 각 화면은 이를 수신하여 누락없이 즉시 반영한다.
 *   (기존 관리자 화면들과 동일하게 WebsocketContext 의 websocketConnect/subscribe 사용)
 *
 *  [입력]
 *   상점명(input) / URL(input, 클릭 시 새창) / 특이사항(textarea) / 상태(on-off 버튼)
 *   인풋은 blur 또는 Enter 시 저장되며, 상태는 클릭 즉시 저장된다.
 * ════════════════════════════════════════════════════════════════════════════════
 */
function AdminStoreSalesPg() {

    const LCAT = localStorage.getItem('adminToken');
    const authHeader = {headers: {Authorization: LCAT}};
    const {websocketConnect, subscribe, unsubscribe, isConnected} = useWebSocket();

    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    /** 로컬 편집중인 값 (seqNum -> {shopName, shopUrl, memo}) */
    const [draft, setDraft] = useState({});

    const [alertShow, setAlertShow] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);
    const modalCancel = useCallback(() => setAlertShow(false), []);

    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    // ────────────────────────────────────────────────────────────────────────
    //  조회
    // ────────────────────────────────────────────────────────────────────────

    const selectList = () => {
        setIsLoading(true);
        axios.get('/storeSales/list', authHeader)
            .then((res) => setRows(res.data || []))
            .catch(() => setRows([]))
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        selectList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ────────────────────────────────────────────────────────────────────────
    //  웹소켓 실시간 공유
    // ────────────────────────────────────────────────────────────────────────

    /** 브로드캐스트 수신 -> 목록 반영 (내가 만든 변경도 동일 경로로 들어오므로 항상 일관된다) */
    const applyBroadcast = useCallback((payload) => {
        let data;
        try {
            data = JSON.parse(payload.body);
        } catch (e) {
            return;
        }
        if (!data || !data.seqNum) return;

        setRows((prev) => {
            if (data.action === 'DELETE') {
                return prev.filter((r) => r.seqNum !== data.seqNum);
            }
            const idx = prev.findIndex((r) => r.seqNum === data.seqNum);
            if (idx < 0) return [data, ...prev];
            const next = [...prev];
            next[idx] = {...next[idx], ...data};
            return next;
        });
        // 서버값으로 확정되었으므로 해당 행의 로컬 편집값은 비운다
        setDraft((prev) => {
            if (!(data.seqNum in prev)) return prev;
            const copy = {...prev};
            delete copy[data.seqNum];
            return copy;
        });
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                await websocketConnect('adminStoreSales');
            } catch (e) { /* 재연결은 WebsocketContext 가 담당한다 */ }
            if (!alive) return;
            subscribe('/topic/storeSales', applyBroadcast);
        })();

        return () => {
            alive = false;
            unsubscribe('/topic/storeSales');
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isConnected]);

    // ────────────────────────────────────────────────────────────────────────
    //  저장 / 삭제
    // ────────────────────────────────────────────────────────────────────────

    const save = (row, patch) => {
        const body = {
            seqNum: row.seqNum,
            shopName: patch.shopName !== undefined ? patch.shopName : (draft[row.seqNum]?.shopName ?? row.shopName ?? ''),
            shopUrl: patch.shopUrl !== undefined ? patch.shopUrl : (draft[row.seqNum]?.shopUrl ?? row.shopUrl ?? ''),
            memo: patch.memo !== undefined ? patch.memo : (draft[row.seqNum]?.memo ?? row.memo ?? ''),
            salesFl: patch.salesFl !== undefined ? patch.salesFl : !!row.salesFl,
        };
        axios.post('/storeSales/save', body, authHeader)
            .then((res) => {
                // 웹소켓이 끊겨있어도 내 화면은 즉시 반영되도록 응답으로도 갱신한다
                if (res.data) applyLocal(res.data);
            })
            .catch(() => {});
    };

    const applyLocal = (data) => {
        setRows((prev) => {
            const idx = prev.findIndex((r) => r.seqNum === data.seqNum);
            if (idx < 0) return [data, ...prev];
            const next = [...prev];
            next[idx] = {...next[idx], ...data};
            return next;
        });
    };

    const addRow = () => {
        axios.post('/storeSales/save', {shopName: '', shopUrl: '', memo: '', salesFl: false}, authHeader)
            .then((res) => {
                if (res.data) applyLocal(res.data);
            })
            .catch(() => {});
    };

    const deleteAlert = (row) => {
        setPendingDelete(row);
        setAlertShow(true);
    };

    const deleteRow = () => {
        if (!pendingDelete) return;
        axios.post('/storeSales/delete', null, {...authHeader, params: {seqNum: pendingDelete.seqNum}})
            .then(() => setRows((prev) => prev.filter((r) => r.seqNum !== pendingDelete.seqNum)))
            .catch(() => {})
            .finally(() => {
                setAlertShow(false);
                setPendingDelete(null);
            });
    };

    const draftChange = (seqNum, key, value) => {
        setDraft((prev) => ({...prev, [seqNum]: {...(prev[seqNum] || {}), [key]: value}}));
    };

    const valueOf = (row, key) => (draft[row.seqNum]?.[key] ?? row[key] ?? '');

    const openUrl = (url) => {
        if (!url) return;
        const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        window.open(href, '_blank', 'noopener,noreferrer');
    };

    const doneCount = rows.filter((r) => r.salesFl).length;

    return (
        <div className="AdminStoreSalesPg">
            <div className="admin-work-title">
                <img className="wd-35 ht-35" alt="" src={maCateImg}/>
                <p className="p-t-6">영업현황</p>
            </div>

            <div className="admin-wrap font-t-cont">
                <div className="admin-wrap-card">

                    <div className="admin-event-wrap" style={{marginBottom: "12px"}}>
                        <div className="admin-event-wrap-btn" style={{display: "flex", alignItems: "center", gap: "10px"}}>
                            <span className="font-s-13" style={{color: "#8A8A8A"}}>
                                전체 <b style={{color: "#3A3A3A"}}>{rows.length}</b>
                                <span style={{margin: "0 6px", opacity: 0.4}}>|</span>
                                영업완료 <b style={{color: "#4E9A63"}}>{doneCount}</b>
                                <span style={{margin: "0 6px", opacity: 0.4}}>|</span>
                                영업대기 <b style={{color: "#D06B6B"}}>{rows.length - doneCount}</b>
                            </span>
                            <span className="font-s-12" style={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                color: isConnected ? "#4E9A63" : "#B0B0B0",
                            }}>
                                <span style={{
                                    width: "6px", height: "6px", borderRadius: "50%",
                                    background: isConnected ? "#7FCBA4" : "#CFCFCF",
                                }}/>
                                {isConnected ? '실시간 공유중' : '연결대기'}
                            </span>
                        </div>
                        <div className="admin-event-wrap-btn gap10">
                            <button className="bt-btn-A wd-110 ht-32 font-s-13 btn-click-event" onClick={addRow}>
                                <FiPlus style={{verticalAlign: "-2px", marginRight: "4px"}}/>목록추가
                            </button>
                        </div>
                    </div>

                    {/* ══ 목록 ══ */}
                    <div style={{border: "1px solid #EDEDED", borderRadius: "12px", overflow: "hidden", background: "#FFFFFF"}}>
                        <div style={{
                            display: "flex", alignItems: "center", height: "46px",
                            background: "#F5F5F5", fontSize: "13px", fontWeight: "bold", color: "#777",
                        }}>
                            <div style={{width: "60px", textAlign: "center"}}>번호</div>
                            <div style={{width: "220px", padding: "0 10px"}}>상점명</div>
                            <div style={{width: "300px", padding: "0 10px"}}>URL</div>
                            <div style={{flex: "1 1 0", padding: "0 10px", minWidth: 0}}>특이사항</div>
                            <div style={{width: "130px", textAlign: "center"}}>상태</div>
                            <div style={{width: "70px", textAlign: "center"}}>삭제</div>
                        </div>

                        {rows.length === 0 ?
                            <div style={{padding: "56px 20px", textAlign: "center", color: "#AAA", fontSize: "14px"}}>
                                {isLoading ? '조회중입니다' : '[목록추가] 버튼으로 영업할 쇼핑몰을 등록해주세요'}
                            </div>
                            :
                            rows.map((row, i) => (
                                <div key={row.seqNum} style={{
                                    display: "flex", alignItems: "flex-start",
                                    borderTop: "1px solid #F1F1F1", padding: "10px 0",
                                }}>
                                    <div style={{width: "60px", textAlign: "center", paddingTop: "9px", fontSize: "13px", color: "#999"}}>
                                        {rows.length - i}
                                    </div>

                                    {/* 상점명 */}
                                    <div style={{width: "220px", padding: "0 10px"}}>
                                        <input className="admin-input-line ht-34 font-s-13" maxLength={60}
                                               style={{width: "100%", padding: "0 8px"}}
                                               placeholder="상점명"
                                               value={valueOf(row, 'shopName')}
                                               onChange={(e) => draftChange(row.seqNum, 'shopName', e.target.value)}
                                               onBlur={() => save(row, {shopName: valueOf(row, 'shopName')})}
                                               onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}/>
                                    </div>

                                    {/* URL */}
                                    <div style={{width: "300px", padding: "0 10px", display: "flex", alignItems: "center", gap: "6px"}}>
                                        <input className="admin-input-line ht-34 font-s-13" maxLength={300}
                                               style={{flex: 1, minWidth: 0, padding: "0 8px"}}
                                               placeholder="https://"
                                               value={valueOf(row, 'shopUrl')}
                                               onChange={(e) => draftChange(row.seqNum, 'shopUrl', e.target.value)}
                                               onBlur={() => save(row, {shopUrl: valueOf(row, 'shopUrl')})}
                                               onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}/>
                                        <FiExternalLink title="새창으로 열기"
                                                        onClick={() => openUrl(valueOf(row, 'shopUrl'))}
                                                        style={{
                                                            cursor: valueOf(row, 'shopUrl') ? "pointer" : "default",
                                                            opacity: valueOf(row, 'shopUrl') ? 0.75 : 0.25,
                                                            flex: "0 0 auto", fontSize: "16px",
                                                        }}/>
                                    </div>

                                    {/* 특이사항 */}
                                    <div style={{flex: "1 1 0", padding: "0 10px", minWidth: 0}}>
                                        <textarea className="admin-input-line font-s-13" rows={2} maxLength={1000}
                                                  style={{width: "100%", padding: "7px 8px", resize: "vertical", minHeight: "34px", lineHeight: 1.6}}
                                                  placeholder="특이사항"
                                                  value={valueOf(row, 'memo')}
                                                  onChange={(e) => draftChange(row.seqNum, 'memo', e.target.value)}
                                                  onBlur={() => save(row, {memo: valueOf(row, 'memo')})}/>
                                    </div>

                                    {/* 상태 */}
                                    <div style={{width: "130px", display: "flex", justifyContent: "center", paddingTop: "5px"}}>
                                        <SalesPill on={!!row.salesFl} onClick={() => save(row, {salesFl: !row.salesFl})}/>
                                    </div>

                                    {/* 삭제 */}
                                    <div style={{width: "70px", display: "flex", justifyContent: "center", paddingTop: "9px"}}>
                                        <FiTrash2 title="목록삭제" onClick={() => deleteAlert(row)}
                                                  style={{cursor: "pointer", opacity: 0.55, fontSize: "16px", color: "#D06B6B"}}/>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>

            {alertShow &&
                <MsgSelectModal alertShow={alertShow}
                                eventHandle1={modalCancel}
                                eventHandle2={deleteRow}
                                msgData1={`[${pendingDelete?.shopName || '이름없음'}] 목록이 삭제됩니다.`}
                                msgData2="정말 삭제하시겠습니까?"/>
            }
            <TopBtn/>
        </div>
    );
}

/**
 * 영업완료 / 영업대기 on-off 버튼
 * - [상점정보]의 활성/비활성 UI 와 일관된 파스텔 연두색/빨간색 카드형 UI
 */
function SalesPill({on, onClick}) {
    return (
        <div onClick={onClick}
             style={{
                 cursor: "pointer", userSelect: "none",
                 display: "inline-flex", alignItems: "center", gap: "6px",
                 padding: "6px 14px", borderRadius: "999px",
                 background: on ? "#EAF7EC" : "#FDECEC",
                 border: `1px solid ${on ? "#BEE5C6" : "#F6C9C9"}`,
                 color: on ? "#4E9A63" : "#D06B6B",
                 fontSize: "12.5px", fontWeight: "bold",
                 transition: "background .18s, border-color .18s",
             }}>
            <span style={{width: "7px", height: "7px", borderRadius: "50%", background: on ? "#7FCBA4" : "#F09C9C"}}/>
            {on ? '영업완료' : '영업대기'}
        </div>
    );
}

export default AdminStoreSalesPg;
